import { useEffect, useCallback, useState } from 'react';
import { count as getSMSLengthInfo } from 'sms-length';
import useContact from 'hooks/redux/useContact';
import WarningCircle from 'images/icon-hazard.svg';
import WarningTriangle from 'images/icon-hazard-triangle.svg';

const MAX_NUMBER_OF_SMS_MESSAGES = 10;
const SMS_MAX_LENGTHS: Record<ReturnType<typeof getSMSLengthInfo>['encoding'], number> = {
    GSM_7BIT: 1024,
    GSM_7BIT_EXT: 1024,
    UTF16: 630 // From Pinpoint docs
};

const WHATSAPP_MAX_LENGTH = 1023

const useSocialChatReplyFunctionality = (socialMediaReply: string, subChannel?: string) => {
    const contact = useContact();

    const [textLimit, setTextLimit] = useState<number>(0)

    const isSMS = subChannel === 'SMS-DM' || contact?.attributes?.['sa-social-media-platform'] === 'SMS';

    const isWhatsApp = contact?.attributes?.['sa-social-media-platform'] === 'WHATSAPP';
    const { messages: numberOfSMSMessages } = getSMSLengthInfo(socialMediaReply);

    const getSocialReplyMaxLength = useCallback(() => {
        if (isSMS) {
            return undefined;
        }

        return textLimit;
    }, [socialMediaReply, isSMS, textLimit]);

    const isSocialReplyTextLimitReached = useCallback(() => {
        if (isSMS) {
            const { encoding, length, messages } = getSMSLengthInfo(socialMediaReply);
            const isSMSCharacterLimitReached = length > SMS_MAX_LENGTHS[encoding] || messages > MAX_NUMBER_OF_SMS_MESSAGES;
            return isSMSCharacterLimitReached;
        }

        return socialMediaReply.length > textLimit;
    }, [isSMS, socialMediaReply]);

    const smsIsTooLongToSend = isSMS && isSocialReplyTextLimitReached();
    const willSendMultipleSMSMessages = numberOfSMSMessages > 1 && !isSocialReplyTextLimitReached();

    const getSMSAlertBannerIconDiv = () => document.querySelector('.sa-alert-banner--icon') as HTMLElement;
    const getSMSAlertBannerImg = () => document.querySelector('.sa-alert-banner--icon img');

    useEffect(() => {
        if (subChannel === "TWEET" || contact?.attributes?.['sa-sub-channel'] === "TWEET") {
            setTextLimit(260);
        } else if (isSMS) {
            setTextLimit(SMS_MAX_LENGTHS.GSM_7BIT);
        } else if (isWhatsApp) {
            setTextLimit(WHATSAPP_MAX_LENGTH);
        } 
        else {
            setTextLimit(1024);
        }
    }, [contact?.attributes?.['sa-sub-channel']])

    useEffect(() => {
        if (smsIsTooLongToSend) {
            const alertBannerBox = getSMSAlertBannerIconDiv()
            const alertBannerImg = getSMSAlertBannerImg();
            if (alertBannerBox) {
                alertBannerBox.style.background = '#D06C00';
            }
            if (alertBannerImg) {
                alertBannerImg.setAttribute('src', WarningTriangle);
            }
        }
    }, [smsIsTooLongToSend]);

    useEffect(() => {
        if (willSendMultipleSMSMessages) {
            const alertBannerImg = getSMSAlertBannerImg();
            if (alertBannerImg) {
                alertBannerImg.setAttribute('src', WarningCircle);
            }
        }
    }, [willSendMultipleSMSMessages]);

    return {
        smsIsTooLongToSend,
        willSendMultipleSMSMessages,
        isSMS,
        getSocialReplyMaxLength,
        isSocialReplyTextLimitReached,
        textLimit,
        numberOfSMSMessages
    }
}

export default useSocialChatReplyFunctionality;
